// ---------------------------------------------------------------------------
//  Skill label → radar category + icon mapping
//  Matching is done on the lowercased label returned by the Hudl lexicon
//  (scout_uni_overview_player_skills). The catalog is relatively stable; we
//  fall back to "Scoring" when no match.
// ---------------------------------------------------------------------------

import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import FlagIcon from '@mui/icons-material/Flag';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HubIcon from '@mui/icons-material/Hub';
import StraightenIcon from '@mui/icons-material/Straighten';

export const RADAR_CATEGORIES = [
  { key: 'scoring',    label: 'Scoring',    icon: SportsBasketballIcon },
  { key: 'playmaking', label: 'Playmaking', icon: SyncAltIcon },
  { key: 'rebounding', label: 'Rebounding', icon: FlagIcon },
  { key: 'defense',    label: 'Defense',    icon: ShieldIcon },
  { key: 'iq',         label: 'IQ',         icon: PsychologyIcon },
  { key: 'physical',   label: 'Physical',   icon: FitnessCenterIcon },
];

// Ordered, case-insensitive substring rules. First match wins.
const CATEGORY_RULES = [
  // Scoring
  { cat: 'scoring', patterns: ['3-pt', '3 pt', '3pt', 'three', 'long shot', 'perimeter shot'] },
  { cat: 'scoring', patterns: ['2-pt', 'mid-range', 'midrange', 'mid range'] },
  { cat: 'scoring', patterns: ['free throw', 'free-throw', 'free throws'] },
  { cat: 'scoring', patterns: ['finishing', 'close to basket', 'layup', 'drive', 'cut', 'post up', 'post-up'] },
  { cat: 'scoring', patterns: ['shooting', 'scoring', 'shot'] },

  // Playmaking
  { cat: 'playmaking', patterns: ['assist', 'passing', 'pass'] },
  { cat: 'playmaking', patterns: ['pick and roll', 'pnr', 'pick & roll', 'ballhandler', 'ball handler', 'ball handling', 'handle'] },
  { cat: 'playmaking', patterns: ['decision', 'playmaking', 'creation'] },

  // Rebounding
  { cat: 'rebounding', patterns: ['rebound', 'offensive rb', 'defensive rb', 'box out', 'boxing out'] },

  // Defense
  { cat: 'defense', patterns: ['steal'] },
  { cat: 'defense', patterns: ['block', 'shot contest'] },
  { cat: 'defense', patterns: ['defense', 'defending', 'defender', 'on-ball', 'off-ball', 'help side', 'rotation', 'closeout', 'rim protection'] },

  // IQ
  { cat: 'iq', patterns: ['iq', 'basketball iq', 'positioning', 'awareness', 'spacing', 'reading', 'vision', 'game sense'] },

  // Physical
  { cat: 'physical', patterns: ['athleticism', 'athletic', 'strength', 'speed', 'quickness', 'vertical', 'motor', 'stamina', 'endurance', 'explosiveness'] },
];

export function categoryForSkill(label) {
  if (!label) return 'scoring';
  const low = String(label).toLowerCase();
  for (const rule of CATEGORY_RULES) {
    for (const p of rule.patterns) {
      if (low.includes(p)) return rule.cat;
    }
  }
  return 'scoring';
}

// Pick an MUI icon from a finer-grained heuristic (used on the "Top Skills"
// list for variety). Not exhaustive — falls back to the category icon.
const ICON_HINTS = [
  { icon: BoltIcon,        test: /(3-pt|3pt|three|long shot|perimeter shot)/i },
  { icon: GpsFixedIcon,    test: /(free throw|free-throw)/i },
  { icon: SportsBasketballIcon, test: /(finishing|close to basket|layup|drive|cut|post up|post-up|shot|scoring|shooting|mid-range|midrange)/i },
  { icon: HubIcon,         test: /(assist|passing|pass|decision|playmak|creation)/i },
  { icon: SyncAltIcon,     test: /(pick and roll|pick & roll|pnr|ball handl|handle|ballhandler)/i },
  { icon: FlagIcon,        test: /(rebound|box out|boxing out)/i },
  { icon: BlockIcon,       test: /(block|shot contest)/i },
  { icon: ShieldIcon,      test: /(defense|defending|defender|on-ball|off-ball|help side|rotation|closeout|rim protection|steal)/i },
  { icon: VisibilityIcon,  test: /(iq|positioning|awareness|vision|reading|game sense|spacing)/i },
  { icon: FitnessCenterIcon, test: /(athleticism|strength|explosive|motor|stamina|endurance)/i },
  { icon: StraightenIcon,  test: /(speed|quickness|vertical)/i },
];

export function iconForSkill(label, fallback = SportsBasketballIcon) {
  if (!label) return fallback;
  for (const h of ICON_HINTS) if (h.test.test(label)) return h.icon;
  return fallback;
}

/**
 * Aggregate a list of skills {label, value} by category (mean).
 * Returns an array in RADAR_CATEGORIES order with { key, label, value, icon, count }.
 */
export function aggregateByCategory(skills) {
  const buckets = Object.fromEntries(RADAR_CATEGORIES.map((c) => [c.key, { sum: 0, n: 0 }]));

  for (const s of skills || []) {
    const cat = categoryForSkill(s.label);
    if (!buckets[cat]) continue;
    const v = Number(s.value);
    if (!Number.isFinite(v)) continue;
    buckets[cat].sum += v;
    buckets[cat].n += 1;
  }

  return RADAR_CATEGORIES.map((c) => {
    const b = buckets[c.key];
    const value = b.n > 0 ? b.sum / b.n : 0;
    return { key: c.key, label: c.label, value, icon: c.icon, count: b.n };
  });
}

// Badge labels derived from skill values
// >= 85 → Elite, 70–84 → Strong, 55–69 → Solid, < 40 on key skill → Limited
export function qualifyValue(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  if (v >= 85) return { tone: 'success', label: 'Elite' };
  if (v >= 70) return { tone: 'accent',  label: 'Strong' };
  if (v >= 55) return { tone: 'info',    label: 'Solid' };
  if (v <  40) return { tone: 'warning', label: 'Limited' };
  return null;
}
